import { LinkedinConnectionType, SupportUser } from "../schema/support-dashboard";

const SUCCESS_BADGE_CLASSES = "bg-status-success-light text-status-success";
const NEUTRAL_BADGE_CLASSES = "bg-inactive-item text-text-meta";
const ERROR_BADGE_CLASSES = "bg-status-error-light text-status-error";
const WARNING_BADGE_CLASSES = "bg-status-warning-light text-status-warning";
const BRAND_BADGE_CLASSES = "bg-blue-action-200 text-blue-action";

/** Badge descriptor: i18n key (under `supportDashboard.users`) + tone classes. */
export interface SupportUserBadge {
	labelKey: string;
	classes: string;
}

export const getYesNoBadge = (value: boolean): SupportUserBadge => ({
	labelKey: value ? "supportDashboard.users.yes" : "supportDashboard.users.no",
	classes: value ? SUCCESS_BADGE_CLASSES : NEUTRAL_BADGE_CLASSES,
});

/**
 * Badge for the LinkedIn column: green when healthy, amber when only Sales
 * Navigator is disconnected (base LinkedIn still works), red when the account
 * is logged out or was removed, neutral when the user never connected.
 */
export const getLinkedinConnectionBadge = (user: SupportUser): SupportUserBadge => {
	if (!user.linkedinStatus) {
		return {
			labelKey: "supportDashboard.users.linkedin.notConnected",
			classes: NEUTRAL_BADGE_CLASSES,
		};
	}

	switch (user.linkedinStatus) {
		case "LISN_CREDENTIALS":
			return {
				labelKey: "supportDashboard.users.linkedin.salesNavDisconnected",
				classes: WARNING_BADGE_CLASSES,
			};
		case "CREDENTIALS":
			return {
				labelKey: "supportDashboard.users.linkedin.disconnected",
				classes: ERROR_BADGE_CLASSES,
			};
		case "DELETED":
			return { labelKey: "supportDashboard.users.linkedin.removed", classes: ERROR_BADGE_CLASSES };
		default:
			// OK and any unknown future status: fall back to the connected flag.
			return user.linkedinConnected
				? { labelKey: "supportDashboard.users.linkedin.connected", classes: SUCCESS_BADGE_CLASSES }
				: {
						labelKey: "supportDashboard.users.linkedin.disconnected",
						classes: ERROR_BADGE_CLASSES,
					};
	}
};

export const getConnectionTypeBadgeClasses = (type: LinkedinConnectionType): string =>
	type === "NORMAL" ? NEUTRAL_BADGE_CLASSES : BRAND_BADGE_CLASSES;

export const getFullName = (user: SupportUser): string => {
	const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

	return name || "—";
};
